import React, { useState, useEffect } from "react";
import { Card } from "../../ui/Card/Card";
import { Phone, Mail, User, Users } from "lucide-react";
import { useAuth } from "../../../contexts/AuthContext";
import customerAccessService from "../../../services/customerAccessService";
import userManagementService from "../../../services/userManagementService";

/**
 * Customer Info Card - Dashboard customer overview
 * Displays company info, contact details, and access management
 */
export const CustomerInfoCard = ({ customer }) => {
  const { user } = useAuth();
  const [owner, setOwner] = useState(null);
  const [consultants, setConsultants] = useState([]);
  const [accessLoading, setAccessLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  // Check if user can manage access (admin or consultant)
  const canManageAccess = user?.role === "admin" || user?.role === "consultant";

  // Fetch owner and consultants data (only for admin/consultant)
  useEffect(() => {
    const fetchAccessData = async () => {
      if (!canManageAccess) return; // Skip if user can't manage access

      try {
        setAccessLoading(true);

        // Fetch latest customer data to get updated owner_user_id
        const latestCustomer = await customerAccessService.getCustomerById(
          customer.id
        );

        // Fetch owner if exists
        if (latestCustomer.owner_user_id) {
          const usersResponse = await userManagementService.getAllUsers();
          const users = usersResponse?.data || [];
          const ownerUser = users.find(
            (u) => u.id === latestCustomer.owner_user_id
          );
          setOwner(ownerUser || null);
        } else {
          setOwner(null);
        }

        // Fetch consultants
        const consultantsList =
          await customerAccessService.getCustomerConsultants(customer.id);
        setConsultants(consultantsList || []);
      } catch (error) {
        console.error("Error fetching access data:", error);
      } finally {
        setAccessLoading(false);
      }
    };

    if (customer && canManageAccess) {
      fetchAccessData();
    }
  }, [customer, refreshKey, canManageAccess]);

  if (!customer) {
    return (
      <Card className="p-6">
        <p className="text-gray-700">
          Select a customer to view detailed information
        </p>
      </Card>
    );
  }

  const { name, contactPerson, contactEmail, phone } = customer;

  return (
    <>
      <Card>
        {/* Header Section */}
        <div className="flex items-start justify-between mb-4">
          {/* Company Info */}
          <div className="flex items-start gap-4 flex-1">
            {/* Profile Picture */}
            {customer.imageUrl ? (
              <img
                src={customer.imageUrl}
                alt={`${name} logo`}
                className="w-20 h-20 rounded-lg object-cover flex-shrink-0"
                onError={(e) => {
                  // Fallback to initial if image fails to load
                  e.target.style.display = "none";
                  e.target.nextElementSibling.style.display = "flex";
                }}
              />
            ) : null}
            {/* Fallback to initial letter */}
            <div
              className="w-20 h-20 bg-gradient-to-br from-blue-600 to-indigo-700 rounded-lg flex items-center justify-center flex-shrink-0 shadow-md"
              style={{ display: customer.imageUrl ? "none" : "flex" }}
            >
              <span className="text-2xl font-bold text-white">
                {name?.charAt(0)?.toUpperCase() || "C"}
              </span>
            </div>

            {/* Company Details */}
            <div className="flex-1">
              <h2 className="text-2xl font-bold text-gray-900 mb-1">{name}</h2>
              {customer.description && (
                <p className="text-sm text-gray-600 mb-3">
                  {customer.description}
                </p>
              )}

              {/* Contact Info */}
              <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                {contactPerson && (
                  <div className="flex items-center gap-1">
                    <User className="h-4 w-4" />
                    <span>{contactPerson}</span>
                  </div>
                )}
                {contactEmail && (
                  <div className="flex items-center gap-1">
                    <Mail className="h-4 w-4" />
                    <span>{contactEmail}</span>
                  </div>
                )}
                {phone && (
                  <div className="flex items-center gap-1">
                    <Phone className="h-4 w-4" />
                    <span>{phone}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Access Management Section (Admin/Consultant only) */}
        {canManageAccess && (
          <div className="border-t border-gray-200 pt-4 mt-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Owner */}
              <div>
                <div className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                  <User className="h-4 w-4" />
                  <span>Owner</span>
                </div>
                {accessLoading ? (
                  <div className="h-6 bg-gray-200 rounded animate-pulse"></div>
                ) : owner ? (
                  <div className="text-sm text-gray-700">
                    {owner.firstName} {owner.lastName}
                  </div>
                ) : (
                  <div className="text-sm text-gray-500 italic">
                    No owner assigned
                  </div>
                )}
              </div>

              {/* Consultants */}
              <div>
                <div className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                  <Users className="h-4 w-4" />
                  <span>Consultants</span>
                </div>
                {accessLoading ? (
                  <div className="h-6 bg-gray-200 rounded animate-pulse"></div>
                ) : consultants.length > 0 ? (
                  <div className="text-sm text-gray-700">
                    {consultants.slice(0, 3).map((c, i) => (
                      <span key={c.id}>
                        {c.firstName} {c.lastName}
                        {i < Math.min(consultants.length, 3) - 1 && ", "}
                      </span>
                    ))}
                    {consultants.length > 3 && (
                      <span className="text-gray-500">
                        {" "}
                        (+{consultants.length - 3} more)
                      </span>
                    )}
                  </div>
                ) : (
                  <div className="text-sm text-gray-500 italic">
                    No consultants assigned
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </Card>
    </>
  );
};

export default CustomerInfoCard;
